import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FaSearch, FaBoxOpen, FaMapMarkerAlt, FaWeight, 
  FaCube, FaInfoCircle, FaExclamationCircle,
  FaCheckCircle, FaClock, FaTruck, FaHome
} from 'react-icons/fa';
import axios from 'axios';
import '../../styles/client.css';

const TrackingPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState(code || '');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');

  // URL параметрээр хайлт хийх
  useEffect(() => {
    if (code) {
      handleSearch(code);
    }
  }, [code]);

  const handleSearch = async (term = searchTerm) => {
    const searchValue = term.trim();
    
    if (!searchValue) {
      setError('Ачааны код эсвэл утасны дугаар оруулна уу!');
      return;
    }

    setLoading(true);
    setError('');
    setResults([]);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/tracking/${searchValue}`
      );

      if (response.data.success) {
        setResults(response.data.data || []);
        if (response.data.data.length === 0) {
          setError('Ачаа олдсонгүй. Код эсвэл утасны дугаараа шалгана уу.');
        }
      }
    } catch (err) {
      console.error('Tracking error:', err);
      setError(
        err.response?.data?.msg || 
        'Ачаа олдсонгүй. Код эсвэл утасны дугаараа шалгана уу.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/tracking/${searchTerm.trim()}`);
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      'registered': <FaCheckCircle className="text-green-500" />,
      'shipped': <FaTruck className="text-blue-500" />,
      'arrived': <FaCheckCircle className="text-purple-500" />,
      'pending_distribution': <FaClock className="text-yellow-500" />,
      'distributed': <FaCheckCircle className="text-green-600" />,
      'on_hold': <FaExclamationCircle className="text-red-500" />,
      'customs_processing': <FaInfoCircle className="text-orange-500" />
    };
    return icons[status] || <FaInfoCircle className="text-gray-500" />;
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      'registered': 'tracking-status-badge status-success',
      'shipped': 'tracking-status-badge status-primary',
      'arrived': 'tracking-status-badge status-purple',
      'pending_distribution': 'tracking-status-badge status-warning',
      'distributed': 'tracking-status-badge status-success',
      'on_hold': 'tracking-status-badge status-danger',
      'customs_processing': 'tracking-status-badge status-orange'
    };
    return classes[status] || 'tracking-status-badge status-default';
  };

  return (
    <div className="tracking-page">
      {/* SEARCH SECTION */}
      <section className="tracking-search-section">
        <div className="container-custom">
          <div className="tracking-search-header">
            <h1 className="tracking-page-title">
              <FaBoxOpen className="inline mr-3" />
              Ачаа хайх
            </h1>
            <p className="tracking-page-subtitle">
              Ачааны код эсвэл утасны дугаараа оруулж ачаагаа хайна уу
            </p>
          </div>

          <form onSubmit={handleSubmit} className="tracking-search-form">
            <div className="tracking-search-input-group">
              <FaSearch className="tracking-search-icon" />
              <input
                type="text"
                placeholder="Ачааны код эсвэл утасны дугаар оруулах..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="tracking-search-input"
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              className="tracking-search-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="btn-spinner"></div>
                  <span>Хайж байна...</span>
                </>
              ) : (
                <>
                  <FaSearch />
                  <span>Хайх</span>
                </>
              )}
            </button>
          </form>

          <div className="tracking-search-hints">
            <p>💡 <strong>Зөвлөмж:</strong> Ачааны код (жнх: ABC-DEF-1234-C1) эсвэл утасны дугаар (жнх: 99112233) оруулна уу</p>
          </div>
        </div>
      </section>

      {/* RESULTS SECTION */}
      <section className="tracking-results-section">
        <div className="container-custom">
          {/* ERROR MESSAGE */}
          {error && (
            <div className="tracking-error">
              <FaExclamationCircle className="tracking-error-icon" />
              <div>
                <h3 className="tracking-error-title">Ачаа олдсонгүй</h3>
                <p className="tracking-error-message">{error}</p>
              </div>
            </div>
          )}

          {/* RESULTS */}
          {!loading && !error && results.length > 0 && (
            <div className="tracking-results">
              <div className="tracking-results-header">
                <h2 className="tracking-results-title">
                  Хайлтын үр дүн ({results.length})
                </h2>
              </div>

              <div className="tracking-cards-grid">
                {results.map((cargo, index) => (
                  <div key={index} className="tracking-card">
                    {/* HEADER */}
                    <div className="tracking-card-header">
                      <div className="tracking-card-title">
                        <FaBoxOpen className="text-blue-600" />
                        <span>{cargo.cargo_name || 'Ачаа'}</span>
                      </div>
                      <div className={getStatusBadgeClass(cargo.status)}>
                        {getStatusIcon(cargo.status)}
                        <span>{cargo.status_label}</span>
                      </div>
                    </div>

                    {/* CARGO CODE */}
                    <div className="tracking-card-section">
                      <div className="tracking-card-label">Ачааны код:</div>
                      <div className="tracking-card-value tracking-code">
                        {cargo.batch_number}
                      </div>
                    </div>

                    {/* QUANTITY */}
                    <div className="tracking-card-section">
                      <div className="tracking-card-label">Тоо ширхэг:</div>
                      <div className="tracking-card-value">
                        <span className="tracking-quantity-badge">
                          {cargo.total_pieces} ширхэг
                        </span>
                      </div>
                    </div>

                    {/* SIZE/WEIGHT */}
                    <div className="tracking-card-section">
                      <div className="tracking-card-label">Хэмжээ:</div>
                      <div className="tracking-card-value">
                        {cargo.cargo_type === 'weight' ? (
                          <span className="flex items-center gap-2">
                            <FaWeight className="text-yellow-600" />
                            <span>{cargo.weight_kg} кг</span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <FaCube className="text-blue-600" />
                            <span>{parseFloat(cargo.volume_cbm || 0).toFixed(4)} м³</span>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* LOCATION */}
                    <div className="tracking-card-section">
                      <div className="tracking-card-label">
                        <FaMapMarkerAlt className="inline mr-1 text-red-500" />
                        Байршил:
                      </div>
                      <div className="tracking-card-value">
                        <div className="tracking-location">
                          <div className="tracking-location-name">
                            {cargo.location?.road_name || 'Тодорхойгүй'}
                          </div>
                          {cargo.location?.road_location && (
                            <div className="tracking-location-detail">
                              {cargo.location.road_location}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* SENDER/RECEIVER */}
                    <div className="tracking-card-divider"></div>
                    <div className="tracking-card-footer">
                      <div className="tracking-party-info">
                        <span className="tracking-party-label">Илгээгч:</span>
                        <span className="tracking-party-name">{cargo.sender_name}</span>
                      </div>
                      <div className="tracking-party-info">
                        <span className="tracking-party-label">Хүлээн авагч:</span>
                        <span className="tracking-party-name">{cargo.receiver_name}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* EMPTY STATE */}
          {!loading && !error && results.length === 0 && !searchTerm && (
            <div className="tracking-empty-state">
              <div className="tracking-empty-icon">
                <FaSearch />
              </div>
              <h3 className="tracking-empty-title">Ачаа хайх</h3>
              <p className="tracking-empty-text">
                Ачааны код эсвэл утасны дугаараа оруулж хайна уу
              </p>
              <button
                onClick={() => navigate('/')}
                className="tracking-empty-btn"
              >
                <FaHome className="mr-2" />
                Нүүр хуудас
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TrackingPage;